import { Link, useNavigation, useSearchParams } from "@remix-run/react";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Input } from "~/components/ui/input";

import type { FormFieldType } from "./form-builder";
import FormBuilder from "./form-builder";

export function ForgotPasswordForm({
  error,
  success,
  message,
  userType = "coach",
}: {
  error?: string;
  success?: boolean;
  message?: string;
  userType?: "coach" | "admin";
}): JSX.Element {
  const [searchParams] = useSearchParams();
  const navigation = useNavigation();
  const emailFromUrl = searchParams.get("email") || undefined;

  const isSubmitting = navigation.state === "submitting";
  const loginRoute = userType === "admin" ? "/admin_login" : "/coach_login";

  const loginFields: FormFieldType[] = [
    {
      id: "forgot-password-email",
      label: "Sähköposti",
      type: "email",
      name: "email",
      placeholder: "nimi@esimerkki.fi",
      defaultValue: emailFromUrl,
      required: true,

      screenReaderDescription:
        "Syötä sähköpostiosoitteesi salasanan vaihtamiseksi",
      Component: Input,
    },
    {
      id: "login-submit",
      label: "",
      name: "submit",
      type: "submit",
      Component: Button,
      children: isSubmitting ? "Lähetetään..." : "Lähetä linkki",
      disabled: isSubmitting,
      screenReaderDescription: "Lähetä salasanan palautuslinkki",
    },
    {
      id: "forgot-password",
      label: "",
      type: "custom",
      name: "back-to-login",
      screenReaderDescription: "Takaisin kirjautumiseen",
      Component: () => (
        <Link
          to={loginRoute}
          className="text-sm text-muted-foreground hover:underline"
        >
          Takaisin kirjautumiseen
        </Link>
      ),
    },
  ];

  return (
    <Card className="w-[350px]" role="main" aria-labelledby="login-title">
      <CardHeader>
        <CardTitle id="login-title">Unohtuiko salasanasi?</CardTitle>
        <CardDescription>
          {success ? message : "Syötä sähköpostiosoitteesi jatkaaksesi"}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {success ? (
          <div className="space-y-4">
            <div className="text-center">
              <p className="text-sm text-green-600">{message}</p>
            </div>
            <Link
              to={loginRoute}
              className="inline-flex h-10 w-full items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
            >
              Takaisin kirjautumiseen
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {error && (
              <div className="text-center text-sm text-red-600">{error}</div>
            )}
            <FormBuilder fields={loginFields} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
